import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountParentAccountInfo, withStyles } from 'vopay';
import styles from './AccountBreadcrumbs.module.scss';

const cn = withStyles(styles);

/** Above this many ancestors the trail collapses to first … last until the user expands it. */
const MAX_VISIBLE_CRUMBS = 2;

type AccountBreadcrumbsProps = {
  /** The account's ancestors, unordered; the chain is built from their parent links. */
  parentAccounts: AccountParentAccountInfo[];
  /** The ancestor that was clicked: the list searches by its name, the account page opens it by id. */
  onCrumbClick: (parentAccount: AccountParentAccountInfo) => void;
};

/**
 * The ancestry trail above an account's name — in the flat (search) table, which is the only route
 * back up the tree once nesting is gone, and on a sub-account's page header, where it is the route to
 * the parents.
 *
 * A component rather than a function the table calls while rendering a cell: it holds state, and a
 * `useState` reached through a plain call inside a cell's render belongs to whatever component owns
 * that render, so it would break as soon as a second templated column took a hook of its own.
 */
export default function AccountBreadcrumbs({ parentAccounts, onCrumbClick }: AccountBreadcrumbsProps) {
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [isExpanded, setIsExpanded] = useState(false);

  // Root first, then down the parent links to the account's immediate parent.
  const trail = useMemo(() => {
    const root = parentAccounts.find((account) => account.ParentAccountID === null);

    if (!root) return [];

    const chain = [root];
    let current = parentAccounts.find((account) => account.ParentAccountID === root.AccountID);

    while (current) {
      const currentId = current.AccountID;

      chain.push(current);
      current = parentAccounts.find((account) => account.ParentAccountID === currentId);
    }

    return chain;
  }, [parentAccounts]);

  if (trail.length === 0) return null;

  const renderCrumb = (parentAccount: AccountParentAccountInfo, index: number) => (
    <Fragment key={parentAccount.AccountID}>
      <button type="button" onClick={() => onCrumbClick(parentAccount)}>
        {parentAccount.AccountName}
      </button>

      {index < trail.length - 1 && ' > '}
    </Fragment>
  );

  const isCollapsed = trail.length > MAX_VISIBLE_CRUMBS && !isExpanded;

  return (
    <nav aria-label={tGlobal('breadcrumbs')}>
      <span className={cn('breadcrumbs')}>
        {isCollapsed ? (
          <>
            {renderCrumb(trail[0], 0)}

            <button type="button" onClick={() => setIsExpanded(true)}>
              ...
            </button>

            {'>'}
            {renderCrumb(trail[trail.length - 1], trail.length - 1)}
          </>
        ) : (
          trail.map(renderCrumb)
        )}
      </span>
    </nav>
  );
}
